// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useEffect, useRef, useLayoutEffect, useState } from 'react';
import './DetectionsView.scss';

/**
 * DetectionsView
 * Renders a pre-loaded image and overlays detection bounding boxes with class labels.
 * Tap the image to toggle detection visibility.
 *
 * Props:
 *  - imageData: Image object { src: string, width: number, height: number }
 *  - boundingBoxes: { [id]: { bbox: [x1, y1, x2, y2], cls: string, conf?: number, labelset?: string } }
 *  - className, style: optional wrapper props
 */
export default function DetectionsView({
  imageData,
  boundingBoxes = {},
  className = '',
  style = {},
  showDetections = true,
  children,
}) {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [initialized, setInitialized] = useState(false);
  const [showDetectionsToggle, setShowDetectionsToggle] = useState(true);

  // Click detection state
  const [clickStartData, setClickStartData] = useState(null);

  // Click detection functions
  const handleClickStart = (event) => {
    setClickStartData({
      x: event.clientX,
      y: event.clientY,
      timestamp: Date.now(),
    });
  };

  const handleClickEnd = (event) => {
    if (!clickStartData) return;

    const deltaX = Math.abs(event.clientX - clickStartData.x);
    const deltaY = Math.abs(event.clientY - clickStartData.y);
    const deltaTime = Date.now() - clickStartData.timestamp;

    // Consider it a click if:
    // - Movement is less than 10px in any direction
    // - Duration is less than 300ms
    if (deltaX < 10 && deltaY < 10 && deltaTime < 300) {
      setShowDetectionsToggle((prev) => !prev);
    }

    setClickStartData(null);
  };

  // Observe container size
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Recompute scale & offset whenever container or image changes
  useEffect(() => {
    if (!imageData || !imageData.width || !imageData.height) return;
    const { width: cw, height: ch } = containerSize;
    if (!cw || !ch) return;

    const s = Math.min(cw / imageData.width, ch / imageData.height);
    setScale(s);
    setOffset({ x: (cw - imageData.width * s) / 2, y: (ch - imageData.height * s) / 2 });
    setInitialized(true);
  }, [imageData, containerSize]);

  return (
    <div
      ref={containerRef}
      className={`detections-view ${className}`}
      style={{ ...style }}
      onMouseDown={handleClickStart}
      onMouseUp={handleClickEnd}
    >
      {imageData && initialized && (
        <img
          src={imageData.src}
          alt=''
          style={{
            position: 'absolute',
            left: `${offset.x}px`,
            top: `${offset.y}px`,
            width: `${imageData.width * scale}px`,
            height: `${imageData.height * scale}px`,
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Detection bounding boxes */}
      {imageData &&
        initialized &&
        Object.entries(boundingBoxes).map(([id, detection]) => {
          if (!detection?.bbox || detection.bbox.length < 4) return null;

          const [x1, y1, x2, y2] = detection.bbox;
          const left = offset.x + x1 * scale;
          const top = offset.y + y1 * scale;
          const width = (x2 - x1) * scale;
          const height = (y2 - y1) * scale;

          // Use color from boundingBox or fallback
          const borderColor = detection.display_color || '#3b82f6';
          const displayName = detection.display_name || detection.cls;

          return (
            <div
              key={id}
              className={`detections-view__detection-container ${showDetectionsToggle && showDetections ? 'visible' : 'hidden'}`}
            >
              <div
                className='detections-view__bbox'
                style={{
                  left,
                  top,
                  width,
                  height,
                  borderColor: `${borderColor}A0`,
                }}
              />
              {/* Class label */}
              <div
                className='detections-view__label'
                style={{
                  left,
                  top: top - 25, // Position above the bounding box
                  backgroundColor: `${borderColor}80`,
                }}
              >
                {displayName}
                {detection.conf && (
                  <span className='detections-view__confidence'>{Math.round(detection.conf * 100)}%</span>
                )}
              </div>
            </div>
          );
        })}
      {children}
    </div>
  );
}
